import { z } from 'zod';
import { ApiError } from './errors.js';
import { LocalizedText, localize } from './i18n.js';
import { OpaqueRef } from './ids.js';
import { MonthDay, inSeason } from './departures.js';
import { PositiveCents, TimeOfDay } from './primitives.js';
/**
 * Lodging (spec §4.7) — the stay, the rate calendar, and the fee that finally
 * earns its keep.
 *
 * §4.7 calls small hotels and high-end rentals "the deliberate stress test,
 * because they bend the time model itself: a stay is **variable-length at night
 * granularity**, priced **per date**, and the inventory is usually **also listed
 * somewhere else**." Three of those five additions live in this file, because
 * all three are arithmetic over documents and none of them is I/O or timezone
 * work:
 *
 *  1. **The rate calendar.** "One `price_cents` cannot price December."
 *  2. **The quote.** `value_cents` is "computed server-side: nights from the
 *     calendar + fees + occupancy tax (USVI: 12.5 %) — B7 doing its usual work,
 *     now over a sum instead of a lookup."
 *  3. **Real cancellation policies.** "A per-item policy of tiers ('free until
 *     14 days out; 50 % until 3; one night inside 3') lets `/cancel` compute and
 *     return a real fee."
 *
 * The other two are elsewhere by the same rule every other vertical followed:
 * night-granularity feasibility is `@yourstore/scheduling`'s (day arithmetic in
 * a tenant's zone is its job and nobody else's), and the unit pool, the external
 * calendars and the book are `@yourstore/store`'s.
 *
 * **There is no parameter anywhere below through which a caller could name a
 * cent.** `quoteStay` takes dates and a calendar and returns money; the request
 * shapes carry no price and there is not going to be one (B7). The caller on the
 * partner side is an LLM agent and it is persuadable; the tools are not.
 */
/* ------------------------------------------------------------------ */
/* 1. The rate calendar                                                */
/* ------------------------------------------------------------------ */
/**
 * **Bands with exceptions, not a row per date** — the shape decision, argued.
 *
 * Spec §4.7 sketches `item_rates` as "per (item, date): nightly price,
 * `min_stay`, closed-to-arrival/departure — with a default row as fallback".
 * That is the right *vocabulary* and the wrong *storage*, and the difference is
 * worth the paragraph:
 *
 *  * **A per-date table is a table nobody can maintain.** Three room types over
 *    two years is ~2,200 rows an owner would have to author, and the only tool
 *    that can author them is a spreadsheet with a formula in it — which is to
 *    say, the bands below, entered by hand, once, with no way to read them back.
 *    What a boutique hotelier actually says is "high season is 15 December to 15
 *    April, four twenty a night, five-night minimum over New Year". That is one
 *    band and one exception.
 *  * **It is read whole, with its row, and never queried across rows.** That is
 *    the same test `tenant_config.hours`, `item_scheduling.turn_by_party_size`
 *    and `item_menu.availability` passed on their way into JSONB: nothing asks
 *    "which items cost $420 on the third of January", and a document that is
 *    always loaded with its parent is a document, not a table.
 *  * **Exceptions stay absolute, bands stay annual.** A band recurs — the season
 *    is a fact about the calendar rather than about 2027, which is the same
 *    argument `Season` made for departures (§4.8) and the same reason both use
 *    `MM-DD` and both wrap the New Year. An *exception* is about one night —
 *    Carnival, the regatta, the night the whole island is full — and a recurring
 *    exception would silently reprice a date twelve months later that nobody
 *    looked at.
 *
 * ## On dev.md's `WeekHours` rule
 *
 * dev.md §6: "Time-of-day availability is always a `WeekHours`/`DayHours`
 * document, never a bespoke shape", with one written-down exception (a departure
 * template is a list of instants). **A rate calendar is the third shape, and it
 * is not an availability document at all.** `WeekHours` answers "are we open at
 * 13:40 on a Tuesday" — a predicate over instants, intersected with other
 * predicates by one property-tested interval algebra. A rate calendar answers
 * "what does the night of the second cost", which is a *function from a calendar
 * date to money*: it has no time of day in it, it is never intersected with
 * anything, and its unit is a night rather than a moment. Squeezing it into
 * `WeekHours` would mean a window per price with the price encoded somewhere
 * outside the document — which is how a price ends up in two places.
 *
 * What it does keep is the discipline that made `WeekHours` worth having: one
 * zod schema, one resolver (`resolveNight`), a small document read whole, and no
 * second place where a night's price can be decided.
 */
export const RateBand = z.object({
    /** What the owner calls it. Shows on a folio line: "High season". */
    label: z.string().min(1).max(80),
    /** `MM-DD`, inclusive at both ends. `to < from` wraps the New Year. */
    from: MonthDay,
    to: MonthDay,
    nightly_cents: PositiveCents,
    /** The arrival-night minimum this band imposes. Absent = the item's own. */
    min_stay_nights: z.number().int().min(1).max(365).optional(),
    closed_to_arrival: z.boolean().default(false),
    closed_to_departure: z.boolean().default(false),
});
/**
 * One dated override — the regatta weekend, the night after a hurricane.
 *
 * Absolute `YYYY-MM-DD`, deliberately (see above). Every field but the date is
 * optional: an exception that only closes arrivals must not have to restate the
 * price, or the day somebody edits the band the exception quietly disagrees.
 */
export const RateException = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD'),
    label: z.string().max(80).optional(),
    nightly_cents: PositiveCents.optional(),
    min_stay_nights: z.number().int().min(1).max(365).optional(),
    closed_to_arrival: z.boolean().optional(),
    closed_to_departure: z.boolean().optional(),
});
/**
 * The whole calendar: a floor, some bands, some exceptions.
 *
 * `base_nightly_cents` is §4.7's "default row as fallback" and it is **required**
 * rather than falling back to `items.price_cents`. A stay item's `price_cents`
 * still exists (every item has one, §7.3.1) and is the "from $X" a listing
 * shows; the *bookable* price is this document's, and letting a night quietly
 * fall through to a column nobody thought of as a rate is exactly how a
 * December night gets sold at the shoulder-season price.
 */
export const RateCalendar = z.object({
    base_nightly_cents: PositiveCents,
    bands: z.array(RateBand).max(24).default([]),
    exceptions: z.array(RateException).max(400).default([]),
});
function bandSeason(band) {
    return { from: band.from, to: band.to };
}
/**
 * Resolve one night against the calendar: **exception, then band, then base.**
 *
 * The precedence is the only one that can be right — an exception exists because
 * the band is wrong for that date — and it is total: every night resolves,
 * because `base_nightly_cents` is required.
 *
 * When two bands cover a date the **last one wins**, and it is written down
 * rather than left to chance: a document is read top to bottom, an owner who
 * adds "Christmas fortnight" under "High season" means the new line to win, and
 * "first wins" would make the fix require deleting the general band. `inSeason`
 * is reused verbatim from §4.8, wrap and all, so "wraps the New Year" has one
 * implementation in this system rather than two.
 */
export function resolveNight(calendar, isoDay) {
    let cents = calendar.base_nightly_cents;
    let source = 'Standard rate';
    let minStayNights = null;
    let closedToArrival = false;
    let closedToDeparture = false;
    for (const band of calendar.bands) {
        if (!inSeason(bandSeason(band), isoDay))
            continue;
        cents = band.nightly_cents;
        source = band.label;
        minStayNights = band.min_stay_nights ?? null;
        closedToArrival = band.closed_to_arrival;
        closedToDeparture = band.closed_to_departure;
    }
    for (const exception of calendar.exceptions) {
        if (exception.date !== isoDay)
            continue;
        if (exception.nightly_cents !== undefined)
            cents = exception.nightly_cents;
        if (exception.label !== undefined && exception.label !== '')
            source = exception.label;
        if (exception.min_stay_nights !== undefined)
            minStayNights = exception.min_stay_nights;
        if (exception.closed_to_arrival !== undefined)
            closedToArrival = exception.closed_to_arrival;
        if (exception.closed_to_departure !== undefined) {
            closedToDeparture = exception.closed_to_departure;
        }
    }
    return { date: isoDay, cents, source, minStayNights, closedToArrival, closedToDeparture };
}
/* ------------------------------------------------------------------ */
/* 2. Fees, tax, and the quote                                         */
/* ------------------------------------------------------------------ */
/**
 * **Per stay or per night, and no third basis.**
 *
 * §4.7 names exactly two — "`item_fees` (cleaning and similar, per-stay or
 * per-night)" — and a third (per guest, per guest per night, a percentage) would
 * be a guess about a business nobody has signed yet. Adding one later is a value
 * in an enum and a branch in one function; guessing now is a shape every tenant
 * carries and one uses.
 */
export const FeeBasis = z.enum(['per_stay', 'per_night']);
/**
 * A fee as the catalog holds it.
 *
 * `name` is a **localized map** (migration 0012) because it is tenant content a
 * guest reads on a quote and hears from a voice agent — the same treatment an
 * item name gets, for the same reason. It is resolved by `quoteStay`, which is
 * allowed to do it because `localize` lives in this package: the one fallback
 * function, called from the one place that turns a fee into a line.
 *
 * `taxable` is a real distinction rather than a flag for symmetry. The USVI
 * hotel room tax is levied on the room charge; a cleaning fee a rental owner
 * collects may or may not be inside it depending on how the stay is sold, and a
 * schema that could not express the difference would make the owner choose
 * between an honest quote and an honest tax return.
 */
export const LodgingFee = z.object({
    code: z.string().min(1).max(40),
    name: LocalizedText,
    amount_cents: PositiveCents,
    basis: FeeBasis.default('per_stay'),
    taxable: z.boolean().default(false),
});
/**
 * The USVI hotel room tax, in basis points (12.5 %) — the number §4.7 names.
 *
 * A default rather than a constant every quote uses: `item_lodging.tax_rate_bps`
 * carries it per item, so a tenant somewhere else, or a rental let long-term and
 * exempt, is a row rather than a fork.
 */
export const USVI_OCCUPANCY_TAX_BPS = 1250;
/**
 * Price a stay from the catalog and **nothing else** (B7, over a sum).
 *
 * Three rules worth reading twice:
 *
 *  * **Tax is rounded once, at the end.** Rounding each night and adding the
 *    remainders drifts by up to a cent a night, which on a fourteen-night stay
 *    is a number the owner cannot reconcile against their own tax return. One
 *    rounding, on the taxable base.
 *  * **A fee is not automatically taxed.** See `LodgingFee.taxable`.
 *  * **An empty stay is refused, not priced at zero.** Zero nights is a caller
 *    that computed a range wrong, and a $0 quote is the shape of answer that
 *    gets confirmed out loud before anybody checks it.
 */
export function quoteStay(input) {
    if (input.nights.length === 0) {
        throw ApiError.invalid('A stay is at least one night; check-out must be after check-in.');
    }
    const nightly = input.nights.map((date) => resolveNight(input.calendar, date));
    const nightlyCents = nightly.reduce((sum, night) => sum + night.cents, 0);
    const fees = input.fees.map((fee) => {
        const qty = fee.basis === 'per_night' ? input.nights.length : 1;
        return {
            code: fee.code,
            name: localize(fee.name, input.locale, input.defaultLocale),
            qty,
            unitPriceCents: fee.amount_cents,
            totalCents: fee.amount_cents * qty,
            taxable: fee.taxable,
        };
    });
    const feesCents = fees.reduce((sum, fee) => sum + fee.totalCents, 0);
    const taxableCents = nightlyCents + fees.filter((f) => f.taxable).reduce((sum, f) => sum + f.totalCents, 0);
    const taxCents = Math.round((taxableCents * input.taxRateBps) / 10_000);
    return {
        nights: input.nights.length,
        nightly,
        nightlyCents,
        fees,
        feesCents,
        subtotalCents: nightlyCents + feesCents,
        taxableCents,
        taxCents,
        totalCents: nightlyCents + feesCents + taxCents,
    };
}
/* ------------------------------------------------------------------ */
/* 3. The cancellation policy                                          */
/* ------------------------------------------------------------------ */
/**
 * What a tier charges. `none` is a value rather than a zero, because "free until
 * 14 days out" is a *decision* and a policy that expressed it as a percentage of
 * nought would read as an unfinished row.
 */
export const CancellationCharge = z.enum(['none', 'percent', 'nights', 'fixed']);
/**
 * One rung: **cancelling at least `days_before` days ahead costs this.**
 *
 * §4.7's own example — "free until 14 days out; 50 % until 3; one night inside
 * 3" — is exactly three rungs:
 *
 * ```
 * { days_before: 14, charge: 'none' }
 * { days_before:  3, charge: 'percent', value: 50 }
 * { days_before:  0, charge: 'nights',  value: 1 }
 * ```
 *
 * and the resolver picks the rung with the **largest `days_before` that is still
 * at or below the days remaining**. Fourteen days out reads the first, four days
 * out the second, the morning of arrival the third.
 */
export const CancellationTier = z.object({
    days_before: z.number().int().min(0).max(365),
    charge: CancellationCharge,
    /** A percentage, a night count, or cents — read according to `charge`. */
    value: PositiveCents.default(0),
});
/**
 * The per-item policy.
 *
 * **A non-empty policy must be total**, and the schema enforces it: there has to
 * be a `days_before: 0` rung, and no two rungs may name the same day. Without
 * the first, a guest cancelling inside the innermost band falls off the bottom
 * of the ladder and the honest answers are "charge nothing" and "throw", both of
 * which are worse than making the operator say what they mean. Without the
 * second, two rungs claim one day and the fee depends on array order.
 *
 * An **empty** policy is free cancellation, and it is what every spa and
 * restaurant tenant has — which is why `fee_cents: 0` for them is literally this
 * code path rather than a branch that skips it (v1.1 §2.6).
 *
 * `policy_ref` is a pointer to the localized paragraph a site renders and an
 * agent reads aloud, exactly as `WeatherPolicy.policy_ref` is: the sentence is
 * tenant content (migration 0012) and does not belong inline in a config blob.
 */
export const CancellationPolicy = z
    .object({
    tiers: z.array(CancellationTier).max(12).default([]),
    policy_ref: z.string().max(200).default(''),
})
    .refine((p) => p.tiers.length === 0 || p.tiers.some((t) => t.days_before === 0), {
    message: 'a cancellation policy needs a days_before: 0 tier, or it is not total',
})
    .refine((p) => new Set(p.tiers.map((t) => t.days_before)).size === p.tiers.length, {
    message: 'two cancellation tiers name the same days_before',
});
export const EMPTY_CANCELLATION_POLICY = { tiers: [], policy_ref: '' };
/**
 * The fee, computed by the provider and never by the agent (v1.1 §2.6).
 *
 * Four rules, each of which a real policy needs:
 *
 *  * **A no-show is the innermost rung**, not an error: `daysOut` below zero is
 *    floored to zero, so a guest who never arrived is charged what a guest
 *    cancelling on the morning would be. Whether that is the *right* charge is
 *    the owner's policy to write; what this must not do is answer "free".
 *  * **`percent` and `nights` are against the accommodation charge**, not
 *    against the total. A hotel that has not cleaned the room does not keep the
 *    cleaning fee, and the government's occupancy tax is not ours to keep at
 *    all — charging a percentage of a tax we would then have to remit is how a
 *    fee becomes a liability.
 *  * **`nights` beyond the stay's length is the whole stay**, not an overcharge.
 *  * **The fee is capped at the accommodation charge.** A `fixed` tier larger
 *    than the stay, on a one-night booking, is a configuration error and not an
 *    instruction to invoice a guest for more than they booked.
 */
export function cancellationFeeCents(policy, input) {
    const daysOut = Math.max(0, Math.trunc(input.daysOut));
    const accommodation = input.nightlyCents.reduce((sum, cents) => sum + cents, 0);
    const applicable = policy.tiers
        .filter((tier) => tier.days_before <= daysOut)
        .sort((a, b) => b.days_before - a.days_before)[0];
    if (applicable === undefined)
        return { feeCents: 0, tier: null, daysOut };
    let feeCents = 0;
    switch (applicable.charge) {
        case 'none':
            feeCents = 0;
            break;
        case 'percent':
            feeCents = Math.round((accommodation * applicable.value) / 100);
            break;
        case 'nights':
            feeCents = input.nightlyCents
                .slice(0, Math.max(0, applicable.value))
                .reduce((sum, cents) => sum + cents, 0);
            break;
        case 'fixed':
            feeCents = applicable.value;
            break;
    }
    return { feeCents: Math.max(0, Math.min(feeCents, accommodation)), tier: applicable, daysOut };
}
/* ------------------------------------------------------------------ */
/* 4. The OTA boundary — refs only                                     */
/* ------------------------------------------------------------------ */
/**
 * An external calendar this unit is also listed on — **the seam, and only the
 * seam** (spec §4.7, addition 4).
 *
 * Two-way iCal was out of the first wave on purpose (real OTA accounts were
 * the gate) and landed on 2026-09-22 (migration 0060, `packages/ical`): the
 * scheduled import resolves this entry's URL from `secret_ref`, lands through
 * `importExternalBusy` into `external_busy` (0021), and records the pull in
 * `external_calendar_pulls` — the freshness lives there, not on this row, so
 * the scheduler never churns a catalog document. This type is still only the
 * *shape*: which unit is listed where, under what credential.
 *
 * **`secret_ref`, never the URL.** An OTA's iCal export address is a bearer
 * credential: anyone holding it can read that unit's whole booking calendar.
 * Storing it in a catalog row a dozen roles can `SELECT` would be the same
 * mistake `webhook_endpoints.secret_ref` and `payments.secret_ref` were designed
 * not to make, so this carries a Secret Manager reference and the puller
 * resolves it at call time (spec §10).
 */
export const ExternalCalendarSource = z.enum(['airbnb', 'vrbo', 'booking', 'ical']);
export const ExternalCalendarRef = z.object({
    /** Stable, operator-chosen, and what an imported busy row is attributed to. */
    ref: z.string().min(1).max(60),
    label: z.string().min(1).max(120),
    source: ExternalCalendarSource,
    /** Which lane of the pool this listing is: room 3 of "Ocean View King" ×4. */
    unit_index: z.number().int().min(0).max(500),
    /** A Secret Manager reference to the iCal URL. Never the URL itself. */
    secret_ref: z.string().max(300).default(''),
    active: z.boolean().default(true),
});
/* ------------------------------------------------------------------ */
/* 5. What a caller may say                                            */
/* ------------------------------------------------------------------ */
/**
 * A stay booking, as a surface asks for one. **No price, and no field that
 * could become one** — the same absence `DepartureBookingRequest` has, for the
 * same structural reason.
 *
 * `check_in`/`check_out` are calendar dates in the tenant's timezone and the
 * stay is `[check_in, check_out)`: a guest arriving on the 2nd and leaving on
 * the 5th has bought three nights, which is the half-open convention this system
 * uses everywhere else, expressed in days rather than in instants.
 */
export const StayBookingRequest = z.object({
    service_id: OpaqueRef,
    check_in: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD'),
    check_out: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD'),
    guests: z.number().int().min(1).max(50).default(1),
    notes: z.string().max(2000).optional(),
});
/** The check-in and check-out wall clocks, and the turnover behind them. */
export const StayTimes = z.object({
    check_in: TimeOfDay,
    check_out: TimeOfDay,
    /** Minutes the unit stays occupied after check-out, for the turn. */
    turnover_min: z.number().int().min(0).max(1440).default(0),
});
/** The nights a `[check_in, check_out)` range covers, as a count. */
export const MAX_STAY_NIGHTS = 365;
//# sourceMappingURL=lodging.js.map